import React from "react";
import { Grid, Box, Typography } from "@mui/material";
import PropTypes from "prop-types";

/**
 * A reusable component for displaying cards with customizable content
 * 
 * @param {Object} props - Component props
 * @param {Array} props.cards - Array of card data objects
 * @param {string} props.cards[].header - Card header text
 * @param {string} props.cards[].value - Card value text
 * @param {string} props.cards[].id - Card unique identifier
 * @param {React.ReactNode} props.cards[].content - Card custom content
 * @param {Object} props.theme - Theme object
 * @param {Object} props.sx - Additional styles
 * @param {boolean} props.fullWidth - Whether the card should take full width
 * @param {number} props.elevation - Shadow depth of the card
 */
const CCard = ({
  cards = [],
  theme,
  sx = {},
  fullWidth = false,
  elevation = 2,
}) => {
  // Create a shadow based on elevation
  const getShadow = (elevation) => {
    const shadows = [
      'none',
      '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
      '0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)',
      '0px 3px 3px -2px rgba(0,0,0,0.2),0px 3px 4px 0px rgba(0,0,0,0.14),0px 1px 8px 0px rgba(0,0,0,0.12)',
      '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
      '0px 3px 5px -1px rgba(0,0,0,0.2),0px 5px 8px 0px rgba(0,0,0,0.14),0px 1px 14px 0px rgba(0,0,0,0.12)',
    ];
    return shadows[Math.min(elevation, 5)];
  };

  return (
    <Grid container spacing={3} sx={sx}>
      {cards.map((card) => (
        <Grid item xs={12} sm={fullWidth ? 12 : 6} key={card.id}>
          <Box
            sx={{
              border: "1px solid",
              borderColor: theme?.palette?.secondary?.main || "rgba(0, 0, 0, 0.12)",
              borderRadius: theme?.shape?.borderRadius || 2,
              p: 3,
              textAlign: "center",
              bgcolor: theme?.palette?.background?.paper || "#ffffff",
              color: theme?.palette?.text?.primary || "#000000",
              boxShadow: getShadow(elevation),
            }}
          >
            {card.content ? (
              card.content
            ) : (
              <>
                <Typography
                  variant="subtitle1"
                  color="inherit"
                  sx={{ fontWeight: "bold" }}
                >
                  {card.header}
                </Typography>
                <Typography variant="h4" color="inherit">
                  {card.value}
                </Typography>
              </>
            )}
          </Box>
        </Grid>
      ))}
    </Grid>
  );
};

CCard.propTypes = {
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      id: PropTypes.string.isRequired,
      content: PropTypes.node,
    })
  ).isRequired,
  theme: PropTypes.object,
  sx: PropTypes.object,
  fullWidth: PropTypes.bool,
  elevation: PropTypes.number,
};

export default CCard;